import React from 'react';
import styled, { keyframes } from 'styled-components';

const SoftwareFeatures = () => {
  const features = [
    {
      id: 1,
      icon: '🔍',
      title: 'Strategic AI Consultation',
      description: 'Expert guidance on implementing AI in your business with a comprehensive roadmap tailored to your specific needs. Book a consultation call for just $199!',
    },
    {
      id: 2,
      icon: '🌐',
      title: 'Custom AI-Powered Websites',
      description: 'Modern, intelligent websites that leverage AI for personalization, chatbots, content generation, and advanced analytics to engage your customers.',
    },
    {
      id: 3,
      icon: '📱',
      title: 'AI-Enhanced Web Applications',
      description: 'Feature-rich web applications powered by cutting-edge AI technologies - from intelligent search to data analysis and automated workflows.',
    },
    {
      id: 4,
      icon: '⚙️',
      title: 'Business Process Automation',
      description: 'Streamline operations with AI-powered automation that eliminates repetitive tasks, reduces errors, and lets your team focus on high-value activities.',
    },
    {
      id: 5,
      icon: '🤖',
      title: 'Custom AI Model Integration',
      description: 'Seamlessly integrate GPT-4, Claude, Gemini and other leading AI models into your existing systems for immediate productivity gains.',
    },
    {
      id: 6,
      icon: '🚀',
      title: 'Rapid Development & Deployment',
      description: 'Expedited timelines with our senior engineering team - delivering production-ready AI solutions in a fraction of the usual development time.',
    },
  ];

  return (
    <FeaturesContainer id="features">
      <SectionHeader>
        <FeatureBadge>OUR SERVICES</FeatureBadge>
        <SectionTitle>AI Solutions To <HighlightText>ACCELERATE YOUR GROWTH</HighlightText></SectionTitle>
        <SectionDescription>
          From strategic consultation to full-scale custom development, we provide <strong>everything you need</strong> to harness AI for competitive advantage.
        </SectionDescription>
      </SectionHeader>
      
      <FeaturesGrid>
        {features.map((feature) => (
          <FeatureCard key={feature.id}>
            <FeatureIcon>{feature.icon}</FeatureIcon>
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDescription>{feature.description}</FeatureDescription>
          </FeatureCard>
        ))}
      </FeaturesGrid>
      
      <ActionCTA>
        <CTAText>Don't let competitors outpace you with AI technology</CTAText>
        <CTAButton href="#contact">Book Your Consultation Today</CTAButton>
      </ActionCTA>
      
      <ComparisonSection>
        <ComparisonTitle>Why Our AI Development Stands Out</ComparisonTitle>
        
        <ComparisonTable>
          <thead>
            <tr>
              <th>Features</th>
              <th>Our Services</th>
              <th>Other Providers</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Developer Experience</td>
              <td><CheckMark>✓</CheckMark> 7+ Years Senior Devs</td>
              <td><XMark>✗</XMark> Junior or Outsourced</td>
            </tr>
            <tr>
              <td>Development Location</td>
              <td><CheckMark>✓</CheckMark> 100% US-Based</td>
              <td><XMark>✗</XMark> Typically Offshore</td>
            </tr>
            <tr>
              <td>Delivery Timeline</td>
              <td><CheckMark>✓</CheckMark> Expedited</td>
              <td><XMark>✗</XMark> Extended</td>
            </tr>
            <tr>
              <td>AI Model Access</td>
              <td><CheckMark>✓</CheckMark> Latest Models</td>
              <td><XMark>✗</XMark> Limited Selection</td>
            </tr>
            <tr>
              <td>Money-Back Guarantee</td>
              <td><CheckMark>✓</CheckMark> 100% Guarantee</td>
              <td><XMark>✗</XMark> Rarely Offered</td>
            </tr>
          </tbody>
        </ComparisonTable>
      </ComparisonSection>
    </FeaturesContainer>
  );
};

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const FeaturesContainer = styled.section`
  padding: 100px 80px;
  background-color: #ffffff;
  
  @media (max-width: 991px) {
    padding: 80px 40px;
  }
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const FeatureBadge = styled.div`
  display: inline-block;
  background-color: #e9f7fe;
  color: #024A5F;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 0.9rem;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HighlightText = styled.span`
  color: #FF4757;
  display: block;
  margin-top: 10px;
`;

const SectionDescription = styled.p`
  font-size: 1.2rem;
  color: #666;
  line-height: 1.6;
  
  strong {
    color: #024A5F;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 60px;
  
  @media (max-width: 991px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: #fff;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #eaeaea;
  animation: ${fadeInUp} 0.8s ease-out;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
    border-color: #024A5F;
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 15px;
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #024A5F;
  margin-bottom: 15px;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
`;

const ActionCTA = styled.div`
  background: linear-gradient(135deg, #024A5F, #0871A4);
  border-radius: 10px;
  padding: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 60px;
  box-shadow: 0 10px 30px rgba(2, 74, 95, 0.2);
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 20px;
    padding: 30px 20px;
  }
`;

const CTAText = styled.p`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  flex: 1;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 20px;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  background: linear-gradient(90deg, #FF4757, #FF6B81);
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
  padding: 15px 30px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 15px rgba(255, 71, 87, 0.3);
  animation: ${pulse} 2s infinite;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 20px rgba(255, 71, 87, 0.4);
  }
`;

const ComparisonSection = styled.div`
  margin-top: 60px;
  animation: ${fadeInUp} 1s ease-out;
`;

const ComparisonTitle = styled.h3`
  font-size: 2rem;
  font-weight: 800;
  color: #333;
  text-align: center;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  overflow: hidden;
  
  th, td {
    padding: 18px 25px;
    text-align: left;
    border-bottom: 1px solid #eaeaea;
  }
  
  th {
    background-color: #024A5F;
    color: white;
    font-weight: 600;
    
    &:first-child {
      width: 40%;
    }
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  tr:nth-child(even) {
    background-color: #f7f9fc;
  }
  
  @media (max-width: 768px) {
    th, td {
      padding: 15px;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    
    th, td {
      padding: 12px 10px;
    }
  }
`;

const CheckMark = styled.span`
  color: #00b894;
  font-weight: bold;
  margin-right: 8px;
`;

const XMark = styled.span`
  color: #ff7675;
  font-weight: bold;
  margin-right: 8px;
`;

export default SoftwareFeatures; 